import { Sex } from "../character/character"

/**
 * Where the game attaches a carried object: the hand socket in canonical 64px LPC frame space.
 * Pure data and geometry, shared by the client's held-object sprite and Sprite Lab's pose guides,
 * so the authoring tools read the same numbers the game draws with.
 */
export const heldObjectBehindBody = (action: string): boolean =>
    ["back", "left", "right"].includes(action.split("-")[1])

// Hand sockets in canonical 64px LPC frame coordinates. Column 0 is idle;
// walk animations contain columns 1–8. Objects remain upright while the hand
// follows the walking arm, rather than rotating a wall like a weapon.
// Measured at the visible palm on the human bases; keep both sexes explicit
// because their hand motion is not a constant translation of the same cycle.
// Hand names are anatomical: south/east use the character's left hand, west
// uses the right hand. The slash table below tracks these same hands.
const HANDS: Record<Sex, Record<string, [number, number][]>> = {
    male: {
        front: [[44, 48], [44, 48], [44, 46], [44, 46], [44, 46], [44, 48], [44, 49], [43, 50], [44, 49]],
        back: [[43, 48], [43, 48], [43, 46], [42, 44], [43, 46], [43, 48], [43, 49], [43, 50], [43, 49]],
        left: [[24, 47], [25, 49], [26, 49], [26, 49], [24, 48], [22, 48], [25, 48], [27, 49], [27, 48]],
        right: [[39, 47], [38, 49], [37, 49], [37, 49], [39, 48], [41, 48], [38, 48], [36, 49], [36, 48]],
    },
    female: {
        front: [[43, 48], [43, 48], [43, 47], [42, 47], [43, 47], [43, 48], [42, 49], [41, 49], [43, 49]],
        back: [[42, 49], [42, 49], [42, 48], [41, 47], [42, 48], [42, 49], [42, 50], [41, 51], [42, 50]],
        left: [[26, 47], [26, 49], [27, 50], [26, 49], [23, 48], [22, 48], [24, 49], [25, 49], [26, 48]],
        right: [[37, 47], [37, 49], [36, 50], [37, 49], [40, 48], [41, 48], [39, 49], [38, 49], [37, 48]],
    },
}

// Slash columns 0–5. These follow the gripping hand through the swing, rather
// than reusing the walking wrist position while the arm crosses the body.
const SLASH_HANDS: Record<Sex, Record<string, [number, number][]>> = {
    male: {
        back: [[43, 47], [41, 43], [38, 40], [41, 42], [50, 31], [52, 36]],
        left: [[24, 45], [24, 43], [22, 46], [18, 42], [20, 24], [23, 33]],
        front: [[44, 47], [38, 47], [30, 47], [40, 46], [50, 42], [50, 38]],
        right: [[39, 45], [43, 47], [41, 46], [47, 43], [40, 33], [40, 33]],
    },
    female: {
        back: [[43, 47], [38, 42], [37, 39], [39, 40], [50, 31], [52, 36]],
        left: [[26, 45], [24, 43], [22, 47], [18, 42], [22, 24], [23, 32]],
        front: [[44, 47], [38, 47], [30, 47], [40, 46], [49, 43], [50, 40]],
        right: [[37, 45], [43, 48], [41, 46], [47, 43], [40, 32], [40, 32]],
    },
}

export const heldObjectSocket = (action: string, frame: number, sex: Sex): { x: number; y: number } | undefined => {
    const [verb, direction] = action.split("-")
    const hands = (verb === "slash" ? SLASH_HANDS : HANDS)[sex]?.[direction]
    if (!["idle", "walk", "slash"].includes(verb) || !hands) {
        return undefined
    }
    const column = verb === "slash" ? frame % 6 : verb === "idle" ? 0 : (frame % 8) + 1
    const [x, y] = hands[column]
    return { x, y }
}
